import { Plus, SlidersHorizontal, Trash2 } from "lucide-react";
import type { CommercialDocumentInput } from "../../shared/commercial-documents";
import {
  COMMERCIAL_TERM_GROUPS,
  CommercialTermCategorySchema,
  commercialTermCategory,
  commercialTermGroups,
  type CommercialTerm,
  type CommercialTermCategory,
} from "../../shared/commercial-terms";
import { Button } from "../components/ui/button";
import { Field, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

type Props = { value: CommercialDocumentInput; onChange: (value: CommercialDocumentInput) => void };
type TermProps = {
  row: CommercialTerm;
  index: number;
  onChange: (row: CommercialTerm) => void;
};
const presets = {
  fixed: [
    { action: "Add fee", label: "Development fee", discount: false },
    { action: "Add credit / discount", label: "Development credit", discount: true },
  ],
  monthly: [
    { action: "Add subscription", label: "Monthly subscription", discount: false },
    { action: "Add subscription discount", label: "Monthly subscription discount", discount: true },
  ],
  annual: [
    { action: "Add annual subscription", label: "Annual subscription", discount: false },
    { action: "Add annual discount", label: "Annual subscription discount", discount: true },
  ],
  terms: [
    { action: "Add term", label: "", discount: false },
    { action: "Add minor changes", label: "Included minor changes", discount: false },
    { action: "Add payment terms", label: "Payment terms", discount: false },
    { action: "Add billing schedule", label: "Billing schedule", discount: false },
  ],
};
const groupDescriptions = {
  fixed: "Development, setup, and one-time credits.",
  monthly: "Recurring charges and subscription discounts.",
  annual: "Enter the full annual price, payable upfront, and any annual discount.",
  terms: "What's included, payment terms, and billing schedules.",
};

export function CommercialTerms({ value, onChange }: Props) {
  const update = (row: CommercialTerm) => {
    onChange({
      ...value,
      commercialTerms: value.commercialTerms.map((item) => (item.id === row.id ? row : item)),
    });
  };
  const remove = (id: string) => {
    onChange({
      ...value,
      commercialTerms: value.commercialTerms.filter((item) => item.id !== id),
    });
  };
  return (
    <div className="space-y-7">
      <div>
        <h2 className="text-lg font-semibold">Pricing & terms</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Add only the one-time, monthly, or annual charges that apply. Enter the agreed amounts;
          totals aren't calculated.
        </p>
      </div>
      {commercialTermGroups(value.commercialTerms).map((group) => (
        <section key={group.category} aria-label={group.title} className="space-y-3">
          <div>
            <h3 className="font-semibold">{group.title}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {groupDescriptions[group.category]}
            </p>
          </div>
          {group.rows.length === 0 ? (
            <p className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">
              No items. Add only what applies.
            </p>
          ) : null}
          <div className="space-y-2">
            {group.rows.length > 0 && group.category !== "terms" ? (
              <div
                aria-hidden="true"
                className="hidden grid-cols-[minmax(0,1fr)_minmax(0,0.65fr)_88px] gap-2 px-3 text-xs font-semibold text-muted-foreground sm:grid"
              >
                <span>Description</span>
                <span>Amount</span>
              </div>
            ) : null}
            {group.rows.map((row) => (
              <CommercialTermFields
                key={row.id}
                row={row}
                index={value.commercialTerms.findIndex((item) => item.id === row.id) + 1}
                onChange={update}
                onRemove={() => {
                  remove(row.id);
                }}
              />
            ))}
          </div>
          <AddTerms value={value} onChange={onChange} category={group.category} />
        </section>
      ))}
    </div>
  );
}

function AddTerms({ value, onChange, category }: Props & { category: CommercialTermCategory }) {
  return (
    <div className="flex flex-wrap gap-x-2 gap-y-1">
      {presets[category].map((preset) => (
        <Button
          key={preset.action}
          type="button"
          variant="ghost"
          className="px-2 text-link hover:text-link"
          disabled={value.commercialTerms.length >= 40}
          onClick={() => {
            onChange({
              ...value,
              commercialTerms: [
                ...value.commercialTerms,
                {
                  id: `term_${crypto.randomUUID().replaceAll("-", "")}`,
                  label: preset.label,
                  value: "",
                  category,
                  discount: preset.discount,
                  duration: "",
                },
              ],
            });
          }}
        >
          <Plus />
          {preset.action}
        </Button>
      ))}
    </div>
  );
}

function CommercialTermFields({
  row,
  index,
  onChange,
  onRemove,
}: TermProps & { onRemove: () => void }) {
  const category = commercialTermCategory(row);
  return (
    <div className="relative space-y-2 rounded-md border bg-card p-3">
      <div className="grid items-start gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,0.65fr)_88px]">
        <Field
          className={category === "terms" ? "pr-[88px] sm:col-span-2 sm:pr-0" : "pr-[88px] sm:pr-0"}
        >
          <FieldLabel htmlFor={`${row.id}-label`} className="sr-only">
            Item {index}
          </FieldLabel>
          <Input
            id={`${row.id}-label`}
            value={row.label}
            maxLength={200}
            placeholder="Item description"
            onChange={(event) => {
              onChange({ ...row, label: event.target.value });
            }}
          />
        </Field>
        <Field className={category === "terms" ? "sm:col-span-3 sm:row-start-2" : ""}>
          <FieldLabel htmlFor={`${row.id}-value`} className="sr-only">
            Amount / term {index}
          </FieldLabel>
          <Textarea
            id={`${row.id}-value`}
            rows={1}
            className={
              category === "terms"
                ? "min-h-20 field-sizing-content"
                : "min-h-10 field-sizing-content tabular-nums"
            }
            maxLength={1000}
            value={row.value}
            placeholder={
              category === "terms"
                ? "Describe what's included or agreed…"
                : row.discount
                  ? "$50 or 20%"
                  : "$0.00"
            }
            onChange={(event) => {
              onChange({ ...row, value: event.target.value });
            }}
          />
        </Field>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-3 right-3 text-muted-foreground"
          aria-label={`Remove item ${index}`}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </div>
      {row.discount ? (
        <p className="text-xs text-muted-foreground">
          {category === "fixed"
            ? "Credit / discount · deducted from one-time costs"
            : "Discount / credit · amount off"}
        </p>
      ) : null}
      {category === "monthly" || category === "annual" ? (
        <TermDuration row={row} index={index} onChange={onChange} />
      ) : null}
      <TermOptions row={row} index={index} category={category} onChange={onChange} />
    </div>
  );
}

function TermOptions({
  row,
  index,
  category,
  onChange,
}: {
  row: CommercialTerm;
  index: number;
  category: CommercialTermCategory;
  onChange: (row: CommercialTerm) => void;
}) {
  return (
    <details className="group/options">
      <summary
        aria-label={`Options for item ${index}`}
        title="Item options"
        className="absolute top-3 right-14 flex size-10 cursor-pointer list-none items-center justify-center rounded-sm text-muted-foreground outline-none hover:bg-muted focus-visible:shadow-[var(--focus-ring)] group-open/options:bg-muted [&::-webkit-details-marker]:hidden"
      >
        <SlidersHorizontal className="size-4" />
      </summary>
      <div className="grid items-end gap-3 border-t pt-3 sm:grid-cols-2">
        <Field>
          <FieldLabel htmlFor={`${row.id}-category`}>Move to section</FieldLabel>
          <select
            id={`${row.id}-category`}
            aria-label={`Move to section for item ${index}`}
            value={category}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-2 focus-visible:outline-ring"
            onChange={(event) => {
              const next = CommercialTermCategorySchema.parse(event.target.value);
              onChange({
                ...row,
                category: next,
                discount: next === "terms" ? false : row.discount,
                duration: next === "monthly" || next === "annual" ? row.duration : "",
              });
            }}
          >
            {Object.entries(COMMERCIAL_TERM_GROUPS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        {category !== "terms" ? (
          <label className="flex min-h-10 items-center gap-2 text-sm">
            <input
              type="checkbox"
              aria-label={`Apply as a discount / credit for item ${index}`}
              checked={row.discount ?? false}
              onChange={(event) => {
                onChange({ ...row, category, discount: event.target.checked });
              }}
            />
            Apply as a discount / credit
          </label>
        ) : null}
      </div>
    </details>
  );
}

function TermDuration({
  row,
  index,
  onChange,
}: {
  row: CommercialTerm;
  index: number;
  onChange: (row: CommercialTerm) => void;
}) {
  return (
    <Field className="gap-1.5">
      <FieldLabel htmlFor={`${row.id}-duration`}>
        {row.discount ? "Discount period" : "Subscription period (optional)"}
      </FieldLabel>
      <Input
        id={`${row.id}-duration`}
        aria-label={
          row.discount
            ? `Discount period for item ${index} (required)`
            : `Subscription period (optional) for item ${index}`
        }
        value={row.duration ?? ""}
        maxLength={300}
        placeholder={
          commercialTermCategory(row) === "annual"
            ? row.discount
              ? "e.g. First year, or Ongoing"
              : "e.g. 1 year from go-live, renewed annually"
            : row.discount
              ? "e.g. First 3 months, or Ongoing"
              : "e.g. 12 months from go-live"
        }
        onChange={(event) => {
          onChange({ ...row, duration: event.target.value });
        }}
      />
    </Field>
  );
}
